<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onCreate">
    <v-card tile>
      <v-card-title class="grey lighten-2">Länka avvikelse</v-card-title>

      <v-card-text class="mt-2">
        <v-row>
          <v-col>
            <v-card class="d-flex pa-4">
              <div class="flex-grow-1">
                <div class="text--secondary">Tågnr.</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ delay.train_number }}
                </div>
              </div>
              <div class="flex-grow-1">
                <div class="text--secondary">Plats</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ item._location_place }}
                </div>
              </div>
              <div class="flex-grow-1">
                <div class="text--secondary">Platstid</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ item._location_time }}
                </div>
              </div>
              <div class="flex-grow-1">
                <div class="text--secondary">Försening</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ delay.delay }}min
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              :items="groupedEvents"
              :loading="$apollo.loading"
              :rules="[required]"
              item-value="uuid"
              item-text="title"
              outlined
              placeholder="Välj händelse att länka på..."
              label="Händelser"
              v-model="model.eventUUID"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              :items="searchDelayReasons"
              :loading="$apollo.loading"
              item-value="slug"
              item-text="slug"
              clearable
              outlined
              placeholder="Välj en orsak att länka..."
              label="Orsaker"
              v-model="model.delayReasonSlug"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              :items="['VP', 'OP']"
              clearable
              outlined
              placeholder="Ansvar"
              label="Ansvar"
              v-model="model.responsible"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              outlined
              clearable
              placeholder="Kommentar"
              label="Kommentar"
              v-model="model.comment"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              outlined
              clearable
              type="number"
              placeholder="THR nummer"
              label="THR nummer"
              v-model.number="model.thr"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              outlined
              type="number"
              clearable
              placeholder="ÖDOC nummer"
              label="ÖDOC nummer"
              v-model.number="model.odoc"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer />
        <v-btn rounded @click="closeModal">Avbryt</v-btn>
        <v-btn rounded type="submit" color="primary" :loading="pending"
          >Länka</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import gql from 'graphql-tag'
import { required } from '@/common/validationRules'
import { get } from 'vuex-pathify'
import * as uuid from 'uuid/v4'
import { addDays, format } from 'date-fns'

export default {
  name: 'LinkDeviationDelayModal',

  props: {
    item: Object
  },

  data: () => ({
    required,
    valid: true,
    pending: false,
    model: {
      eventUUID: null,
      delayReasonSlug: null
    }
  }),

  created() {
    this.$store.set('delay/model', {
      uuid: uuid(),
      location_time: this.item.location_time,
      location_place: this.item.location_place,
      delay: this.item.delay_minutes,
      train_number: this.item.train_number,
      type: 'RST',
      checked: true
    })

    if (this.item.delay_reason_description) {
      const [description] = this.item.delay_reason_description.split('(')
      this.model.delayReasonSlug = `${this.item.delay_place.toUpperCase()} ${description} (${
        this.item.level1_code
      }${this.item.level2_code} ${this.item.level3_code})`
    }
    if (this.item.comment) {
      this.model.comment = this.item.comment
    }
    if (this.item.responsible) {
      this.model.responsible = this.item.responsible
    }
    if (this.item.delay) {
      this.model.eventUUID = this.item.delay.event.uuid
    }
    if (this.item.thr) {
      this.model.thr = this.item.thr
    }
    if (this.item.odoc) {
      this.model.odoc = this.item.odoc
    }
  },

  apollo: {
    searchDelayReasons: {
      query: gql`
        query searchDelayReasons($filter: SearchDelayReasonsInput!) {
          searchDelayReasons(filter: $filter) {
            slug
            train_number
            place
            level1_code
            level2_code
            level3_code
            delay_reason_description
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            train_number: this.item.train_number,
            departure_date: this.item.departure_date
          }
        }
      }
    },
    events: {
      query: gql`
        query events($filter: GetEventsInput!) {
          events(filter: $filter) {
            uuid
            title
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            archived: true,
            district_id: this.$store.get('app/selectedDistrictId'),
            from: format(
              addDays(new Date(this.item.advertised_location_time), -5),
              'yyyy-MM-dd'
            ),
            to: format(
              new Date(this.item.advertised_location_time),
              'yyyy-MM-dd'
            )
          }
        }
      }
    },

    activeEvents: {
      fetchPolicy: 'no-cache',
      query: gql`
        query activeEvents {
          activeEvents {
            uuid
            title
            district_id
          }
        }
      `
    }
  },

  computed: {
    delay: get('delay/model'),

    groupedEvents() {
      const activeEvents = (this.activeEvents || []).filter(
        ev => ev.district_id === this.$store.get('app/selectedDistrictId')
      )
      const archivedEvents = this.events || []
      return [
        ...(activeEvents.length ? [{ divider: true, header: 'Aktiva' }] : []),
        ...activeEvents,
        ...(archivedEvents.length
          ? [{ divider: true, header: 'Arkiverade' }]
          : []),
        ...archivedEvents
      ]
    }
  },

  methods: {
    async onCreate() {
      const isValidForm = await this.$refs.form.validate()
      if (!isValidForm) return
      this.pending = true

      this.$store.set('delay/model@event_uuid', this.model.eventUUID)

      const trvDelayUpdatePayload = {
        responsible: this.model.responsible || undefined,
        comment: this.model.comment || undefined,
        thr: this.model.thr,
        odoc: this.model.odoc
      }

      if (this.item.responsible && !trvDelayUpdatePayload.responsible) {
        trvDelayUpdatePayload.responsible = null
      }

      if (this.item.comment && !trvDelayUpdatePayload.comment) {
        trvDelayUpdatePayload.comment = null
      }

      if (this.item.delay_reason_description && !this.model.delayReasonSlug) {
        trvDelayUpdatePayload.delay_place = null
        trvDelayUpdatePayload.level1_code = null
        trvDelayUpdatePayload.level2_code = null
        trvDelayUpdatePayload.level3_code = null
        trvDelayUpdatePayload.delay_reason_description = null
      }

      if (this.model.delayReasonSlug) {
        const refDelayReason = this.searchDelayReasons.find(
          x => x.slug === this.model.delayReasonSlug
        )
        if (refDelayReason) {
          trvDelayUpdatePayload.delay_place = refDelayReason.place
          trvDelayUpdatePayload.level1_code = refDelayReason.level1_code
          trvDelayUpdatePayload.level2_code = refDelayReason.level2_code
          trvDelayUpdatePayload.level3_code = refDelayReason.level3_code
          trvDelayUpdatePayload.delay_reason_description =
            refDelayReason.delay_reason_description
        }
      }

      await this.updateUnhandledDelay({
        trv_delay_id: this.item.id,
        patch: trvDelayUpdatePayload
      })

      if (this.item.delay) {
        await this.replaceDeviation()
      } else {
        await this.createDeviation()
      }

      this.pending = false
      this.closeModal()
      this.$emit('confirm')
    },

    async updateUnhandledDelay(payload) {
      await this.$apollo.mutate({
        mutation: gql`
          mutation updateUnhandledDelay($payload: UpdateUnhandledDelayInput!) {
            updateUnhandledDelay(payload: $payload) {
              unhandled_delay {
                departure_date
                train_number
                location_place
                location_time
                advertised_location_time
                delay_minutes
                delay_place
                level1_code
                level2_code
                level3_code
                delay_reason_description
                responsible
                comment
              }
            }
          }
        `,
        variables: {
          payload
        }
      })
    },

    async createDeviation() {
      await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: CreateDelayInput!) {
            createDelay(payload: $payload) {
              delay_uuid
            }
          }
        `,
        variables: {
          payload: this.delay
        }
      })
    },

    async replaceDeviation() {
      await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: ReplaceDelayInput!) {
            replaceDelay(payload: $payload) {
              delay_uuid
            }
          }
        `,
        variables: {
          payload: {
            delete_uuid: this.item.delay.uuid,
            payload: this.delay
          }
        }
      })
    },

    closeModal() {
      this.$store.set('linkDeviation/selectedDeviation', null)
      this.$store.commit('delay/resetModel')
    }
  }
}
</script>
